import { Typography } from "antd";
import dayjs from "dayjs";
import {
  BID_REQUEST_STATUS_ENUM,
  BID_REQUEST_TYPE_ENUM,
  BID_STATUSES,
  BidEntity,
  BidRequestStatus,
  DIRECTION_TEXT,
  QUOTE_SELECTION_ENUM,
  RequestProcessingHistoryType,
  SUB_STEP_STATUSES_ENUM,
} from "interfaces";
import { dateFormat, getShortName, isStatusIncluded } from "utils";
import { SubSteps } from "components/CardSteps/CardSteps";
import { DIRECT_PAYMENT_CUSTOMER_LP } from "utils/envConstants";
import { BidHistory } from "stores/BidHistory";

export type StepItem = {
  index: number;
  rawStatus: BidRequestStatus;
  title: string;
  date?: string;
  description: string | JSX.Element | null;

  initiator?: {
    id: string;
    fullName: string;
  };

  isCompleted: boolean;
};

export const getStatuses = (data: BidEntity): BidRequestStatus[] => [
  BID_REQUEST_STATUS_ENUM.DRAFT,
  ...(!data.collectQuotesImmediately && data.type !== BID_REQUEST_TYPE_ENUM.REQUEST_WITH_AGENT
    ? [BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED]
    : []),
  ...(data.type === BID_REQUEST_TYPE_ENUM.REQUEST_WITH_AGENT && data.collectQuotesImmediately
    ? [BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL, BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL_ACCEPTED]
    : data.type === BID_REQUEST_TYPE_ENUM.REQUEST_WITH_AGENT && !data.collectQuotesImmediately
      ? [
          BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL,
          BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL_ACCEPTED,
          BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED,
        ]
      : []),
  BID_REQUEST_STATUS_ENUM.NEW,
  BID_REQUEST_STATUS_ENUM.QUOTE_PROPOSED,
  BID_REQUEST_STATUS_ENUM.FUNDING,
  BID_REQUEST_STATUS_ENUM.FUNDED,
  BID_REQUEST_STATUS_ENUM.EXECUTION,
  BID_REQUEST_STATUS_ENUM.COMPLETED,
];

export const getStatusTitle = (status: BidRequestStatus): string => BID_STATUSES.texts[status] || "Неизвестный статус";

export const getDescription = ({
  bid,
  userRoles,
  processingHistoryTooltip,
  stepStatuses,
}: {
  bid: BidEntity;
  userRoles: any;
  processingHistoryTooltip: Record<string, RequestProcessingHistoryType[]>;
  stepStatuses: BidRequestStatus;
}) => {
  const { fiatReceived, rubReceivedByPlatform, fiatSent, feeRubReceivedByPlatform, destinationWalletVerified } =
    bid.requestProcessing;

  const isCustomer = userRoles.isCustomer;
  const isOperator = userRoles.isOperator;
  const isProvider = userRoles.isProvider;
  const isProviderOrLPRiskManager = userRoles.isProvider || userRoles.isLPRiskManager;

  const isCustomerImp = userRoles.isCustomer && bid.contract.direction === "IMP";
  const isProviderExp = isProviderOrLPRiskManager && bid.contract.direction === "EXP";
  const isRubCheck = !DIRECT_PAYMENT_CUSTOMER_LP && fiatSent && !rubReceivedByPlatform && !fiatReceived;
  const checkRubAmount = (isCustomerImp || isProviderExp) && isRubCheck;

  const nativeAndTokenChecked = isStatusIncluded(bid.status, [
    BID_REQUEST_STATUS_ENUM.COMPLETED,
    BID_REQUEST_STATUS_ENUM.EXECUTION,
  ]);

  function getProcessingHistoryTooltip({
    type,
    showFullName = true,
  }: {
    type: SUB_STEP_STATUSES_ENUM;
    showFullName?: boolean;
  }) {
    if (!processingHistoryTooltip[type]) {
      return;
    }

    const fullName = showFullName
      ? getShortName(
          processingHistoryTooltip[type][0].surname ?? "",
          processingHistoryTooltip[type][0].name ?? "",
          processingHistoryTooltip[type][0].secondName
        )
      : "";
    return {
      date: dateFormat(processingHistoryTooltip[type][0].actionDate ?? "", true),
      userName: fullName,
      userId: processingHistoryTooltip[type][0].userId,
    };
  }

  const destinationWalletVerifiedCheck =
    !!getProcessingHistoryTooltip({
      type: SUB_STEP_STATUSES_ENUM.VERIFY_WALLET_REQUEST,
    }) && destinationWalletVerified;

  const showDestinationVerifiedWalletStep =
    stepStatuses === BID_REQUEST_STATUS_ENUM.DRAFT &&
    (isCustomer || isOperator) &&
    bid.contract.direction === "IMP" &&
    (!destinationWalletVerified || destinationWalletVerifiedCheck);

  if (showDestinationVerifiedWalletStep) {
    return (
      <SubSteps
        items={[
          {
            title: "Кошелек согласован",
            checked: destinationWalletVerifiedCheck,
            tooltipHistory: getProcessingHistoryTooltip({
              type: SUB_STEP_STATUSES_ENUM.VERIFY_WALLET_REQUEST,
            }),
          },
        ]}
      />
    );
  }

  if (stepStatuses === BID_REQUEST_STATUS_ENUM.COMPLETED) {
    return <Typography.Text>ЦВ успешно переведена. Заявка исполнена.</Typography.Text>;
  }

  if (
    isStatusIncluded(stepStatuses, [
      BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDED,
      BID_REQUEST_STATUS_ENUM.AML_FAILED_REFUNDED,
    ])
  ) {
    return <Typography.Text>Заявка отклонена, средства возвращены отправителю.</Typography.Text>;
  }

  if (
    isStatusIncluded(stepStatuses, [
      BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDING,
      BID_REQUEST_STATUS_ENUM.AML_FAILED_REFUNDING,
    ]) &&
    bid.status === BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDING
  ) {
    return "Транзакция обрабатывается";
  }

  if (stepStatuses === BID_REQUEST_STATUS_ENUM.EXPIRED) {
    return "Срок данного предложения истек";
  }

  if (stepStatuses === BID_REQUEST_STATUS_ENUM.FUNDING) {
    const { fiatSent, fiatReceived, tokenReceived, nativeReceived, feeRubReceivedByPlatform, rubReceivedByPlatform } =
      bid.requestProcessing;

    if (DIRECT_PAYMENT_CUSTOMER_LP) {
      const commonSteps = [
        {
          title: "Рубли отправлены",
          checked: fiatSent,
          tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.FIAT_SENT }),
        },
        {
          title: "Рубли получены",
          checked: fiatReceived,
          tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.FIAT_RECEIVED }),
        },
      ];

      if (isCustomer) {
        return (
          <SubSteps
            items={[
              ...commonSteps,
              {
                title: "Обеспечено цифровой валютой",
                checked: nativeAndTokenChecked || tokenReceived,
                tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.TOKEN_RECEIVED }),
              },
            ]}
          />
        );
      }

      if (isOperator || isProviderOrLPRiskManager) {
        return (
          <SubSteps
            items={[
              ...commonSteps,
              {
                title: "Обеспечено токенами",
                checked: nativeAndTokenChecked || tokenReceived,
                tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.TOKEN_RECEIVED }),
              },
              {
                title: "Обеспечено нативной валютой",
                checked: nativeAndTokenChecked || nativeReceived,
                tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.NATIVE_RECEIVED }),
              },
            ]}
          />
        );
      }
    } else {
      // DIRECT_PAYMENT = FALSE
      const rubStep = {
        title: "Обеспечено рублями",
        checked: rubReceivedByPlatform,
        tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.RUB_RECEIVED_BY_PLATFORM }),
      };

      const tokenStep = {
        title: "Обеспечено ЦВ",
        checked: nativeAndTokenChecked || tokenReceived,
        tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.TOKEN_RECEIVED }),
      };

      const nativeStep = {
        title: "Комиссия БЧ",
        checked: nativeAndTokenChecked || nativeReceived,
        tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.NATIVE_RECEIVED }),
      };

      const feeStep = {
        title: "Комиссия оплачена",
        checked: feeRubReceivedByPlatform,
        tooltipHistory: getProcessingHistoryTooltip({
          type: SUB_STEP_STATUSES_ENUM.FEE_RUB_RECEIVED_BY_PLATFORM,
        }),
      };

      const digitalCurrencyStep = {
        title: "Обеспечено цифровой валютой",
        checked: nativeAndTokenChecked || tokenReceived,
        tooltipHistory: getProcessingHistoryTooltip({ type: SUB_STEP_STATUSES_ENUM.TOKEN_RECEIVED }),
      };

      if (isCustomer) {
        return <SubSteps items={[rubStep, feeStep, digitalCurrencyStep]} />;
      }

      if (isProviderOrLPRiskManager) {
        return <SubSteps items={[rubStep, tokenStep, nativeStep]} />;
      }

      if (isOperator) {
        return <SubSteps items={[rubStep, tokenStep, nativeStep, feeStep]} />;
      }
    }

    return null;
  }

  if (
    stepStatuses === BID_REQUEST_STATUS_ENUM.FUNDED &&
    bid.contract.direction === "IMP" &&
    !DIRECT_PAYMENT_CUSTOMER_LP
  ) {
    return (
      <SubSteps
        items={[
          {
            title: "Согласовано УВЭД",
            checked: bid.requestProcessing.customerExecuted,
            tooltipHistory: getProcessingHistoryTooltip({
              type: SUB_STEP_STATUSES_ENUM.CUSTOMER_EXECUTED,
            }),
          },
        ].filter((c) => !!c)}
      />
    );
  }

  if (stepStatuses === BID_REQUEST_STATUS_ENUM.EXECUTION) {
    if (bid.amount && Number(bid.balance.token) >= bid.amount) {
      return <Typography.Text>Транзакция отправляется...</Typography.Text>;
    }
    return <Typography.Text>Транзакция отправлена</Typography.Text>;
  }

  if (stepStatuses === BID_REQUEST_STATUS_ENUM.CANCELED) {
    return <Typography.Text>Заявка отменена</Typography.Text>;
  }

  return null;
};

export const getFeatureStatuses = (status: BidRequestStatus, bid: BidEntity) => {
  const statuses = getStatuses(bid);
  if (status === BID_REQUEST_STATUS_ENUM.AML_FAILED_REFUNDING) {
    return [BID_REQUEST_STATUS_ENUM.AML_FAILED_REFUNDED];
  }
  if (status === BID_REQUEST_STATUS_ENUM.CANCELED) {
    if (!bid.balance?.native || (bid.balance?.native && +bid.balance?.native < 0.00001)) {
      return [];
    } else {
      return [BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDING, BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDED];
    }
  }
  if (status === BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDING) {
    return [BID_REQUEST_STATUS_ENUM.CANCELED_REFUNDED];
  }
  if (status === BID_REQUEST_STATUS_ENUM.AML_FAILED) {
    const index = statuses.indexOf(BID_REQUEST_STATUS_ENUM.FUNDING);
    return statuses.slice(index);
  }

  if (status === BID_REQUEST_STATUS_ENUM.EXPIRED) {
    const index = statuses.indexOf(BID_REQUEST_STATUS_ENUM.NEW);
    return statuses.slice(index);
  }

  const index = statuses.indexOf(status);
  return index !== -1 ? statuses.slice(index + 1) : [];
};
